<template>
  <div class="app-container">
    <div class="page-title">
      <h2>{{ $t('recolte.recapCollectesTitle') }}</h2>
    </div>

    <el-card class="box-card filter-card">
      <div slot="header" class="clearfix">
        <span>{{ $t('general.filter') }}</span>
        <el-button style="float: right; padding: 3px 0" type="text" @click="resetFilters">{{ $t('general.init') }}</el-button>
      </div>
      <div class="filter-container" style="display: flex; flex-wrap: wrap; gap: 12px; align-items: center;">
        <el-select
          v-model="campagneFilter"
          :placeholder="$t('recolte.campagne')"
          style="width: 220px"
          @change="onCampagneChange"
        >
          <el-option
            v-for="(item, index) in campagnes"
            :key="index"
            :label="item.periode"
            :value="index"
          />
        </el-select>

        <el-select
          v-model="filters.controleur_id"
          clearable
          filterable
          :placeholder="$t('recolte.filtreTousCI')"
          style="width: 260px"
          @change="fetchRecap"
        >
          <el-option
            v-for="c in controleursOptions"
            :key="c.id"
            :label="ciLabel(c)"
            :value="c.id"
          />
        </el-select>

        <el-select
          v-model="filters.scoops_id"
          clearable
          filterable
          :placeholder="$t('recolte.filtreScoop')"
          style="width: 220px"
          @change="fetchRecap"
        >
          <el-option
            v-for="s in scoopsOptions"
            :key="s.id"
            :label="s.libelle || s.nom || ('#' + s.id)"
            :value="s.id"
          />
        </el-select>

        <el-select
          v-model="filters.unite_production_id"
          clearable
          filterable
          :placeholder="$t('recolte.filtreUP')"
          style="width: 240px"
          @change="fetchRecap"
        >
          <el-option
            v-for="u in upsOptions"
            :key="u.id"
            :label="u.nom || u.libelle || ('#' + u.id)"
            :value="u.id"
          />
        </el-select>

        <el-select
          v-model="filters.groupement_id"
          clearable
          filterable
          :placeholder="$t('recolte.filtreGroupement')"
          style="width: 240px"
          @change="fetchRecap"
        >
          <el-option
            v-for="g in grpsOptions"
            :key="g.id"
            :label="g.nom || g.libelle || ('#' + g.id)"
            :value="g.id"
          />
        </el-select>

        <el-button v-waves type="primary" icon="el-icon-refresh" @click="fetchRecap">
          {{ $t('general.search') }}
        </el-button>
      </div>
    </el-card>

    <el-card v-loading="loading" class="box-card" style="margin-top: 16px;">
      <div v-if="recap" class="kpi-row">
        <el-alert
          v-if="recap.controleur_nom"
          :title="recap.controleur_nom"
          type="info"
          :closable="false"
          show-icon
          style="margin-bottom: 12px;"
        />
        <el-row :gutter="16">
          <el-col :xs="24" :sm="12" :md="8">
            <div class="kpi"><span class="kpi-label">{{ $t('recolte.kpiCollectes') }}</span><span class="kpi-val">{{ recap.nombre_collectes }}</span></div>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <div class="kpi"><span class="kpi-label">{{ $t('sacs.nombreSacs') }}</span><span class="kpi-val">{{ recap.nombre_sacs }}</span></div>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <div class="kpi"><span class="kpi-label">{{ $t('recolte.kpiTonnes') }}</span><span class="kpi-val">{{ recap.volume_total_tonnes }}</span></div>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <div class="kpi"><span class="kpi-label">{{ $t('recolte.kpiProducteurs') }}</span><span class="kpi-val">{{ recap.nombre_producteurs }}</span></div>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <div class="kpi">
              <span class="kpi-label" :title="$t('recolte.kpiParcellesHint')">{{ $t('recolte.kpiParcelles') }}</span>
              <span class="kpi-val">{{ recap.nombre_parcelles }}</span>
            </div>
          </el-col>
          <el-col v-if="recap.surface_totale_ha != null" :xs="24" :sm="12" :md="8">
            <div class="kpi">
              <span class="kpi-label" :title="$t('recolte.kpiSurfaceTotaleHint')">{{ $t('recolte.kpiSurfaceTotale') }}</span>
              <span class="kpi-val">{{ recap.surface_totale_ha }} {{ $t('recolte.uniteHectares') }}</span>
            </div>
          </el-col>
          <el-col v-if="recap.campagne" :xs="24" :sm="12" :md="8">
            <div class="kpi"><span class="kpi-label">{{ $t('recolte.campagne') }}</span><span class="kpi-val">{{ recap.campagne }}</span></div>
          </el-col>
          <el-col v-if="recap.rendement_standard_tenant_t_par_ha != null" :xs="24" :sm="12" :md="8">
            <div class="kpi">
              <span class="kpi-label">{{ $t('recolte.kpiRendementStandardTenant') }}</span>
              <span class="kpi-val">{{ recap.rendement_standard_tenant_t_par_ha }} {{ $t('recolte.uniteTParHa') }}</span>
            </div>
          </el-col>
        </el-row>
      </div>

      <div
        v-if="recap && recap.repartition_par_producteur && recap.repartition_par_producteur.length"
        class="filter-container"
        style="margin-top: 16px; display: flex; align-items: center; gap: 12px;"
      >
        <el-input
          v-model="producteurSearchKeyword"
          clearable
          :placeholder="$t('recolte.rechercheProducteur')"
          prefix-icon="el-icon-search"
          style="max-width: 320px;"
        />
        <span v-if="producteurSearchKeyword && producteurSearchKeyword.trim()" class="text-muted">
          {{ repartitionProducteursFiltree.length }} / {{ recap.repartition_par_producteur.length }}
        </span>
      </div>

      <el-table
        v-if="recap && recap.repartition_par_producteur"
        :data="repartitionProducteursFiltree"
        border
        fit
        style="width: 100%; margin-top: 16px;"
      >
        <el-table-column type="expand">
          <template slot-scope="props">
            <el-table :data="props.row.repartition_par_parcelle" border size="small" style="width: 100%">
              <el-table-column :label="$t('recolte.parcelle')" min-width="100">
                <template slot-scope="p">
                  <a class="link-like" @click="goParcelle(p.row.id_parcelle)">{{ p.row.code_parcelle }}</a>
                </template>
              </el-table-column>
              <el-table-column :label="$t('parcelle.superficie')" min-width="110" align="right">
                <template slot-scope="p">{{ parcelleSuperficie(p.row) }}</template>
              </el-table-column>
              <el-table-column :label="$t('recolte.estimationRendementParcelle')" min-width="130" align="right">
                <template slot-scope="p">{{ parcelleEstimationRendement(p.row) }}</template>
              </el-table-column>
              <el-table-column :label="$t('recolte.colCollectes')" prop="nombre_de_collecte_sur_la_parcelle" width="110" align="center" />
              <el-table-column :label="$t('sacs.nombreSacs')" prop="nombre_de_sac_collecte_sur_la_parcelle" width="100" align="center" />
              <el-table-column :label="$t('recolte.colTonnes')" min-width="100">
                <template slot-scope="p">{{ p.row.volume_collecte_sur_la_parcelle_tonnes }}</template>
              </el-table-column>
              <el-table-column :label="$t('recolte.rendementEstime')" min-width="120" align="center">
                <template slot-scope="p">
                  <el-tag :type="tagType(p.row.label_color_taux_rendement)" size="mini">{{ p.row.Taux_rendement_estime }} %</el-tag>
                </template>
              </el-table-column>
              <el-table-column :label="$t('recolte.productivite')" min-width="120" align="center">
                <template slot-scope="p">
                  <el-tag :type="tagType(p.row.label_color_taux_produitivite)" size="mini">{{ p.row.indice_productivite_parcelle }} %</el-tag>
                </template>
              </el-table-column>
            </el-table>
          </template>
        </el-table-column>
        <el-table-column :label="$t('producteur.producteur')" min-width="200">
          <template slot-scope="scope">
            <a class="link-like" @click="goProducteur(scope.row.id_producteur)">{{ scope.row.nom_producteur }}</a>
            <span v-if="scope.row.code_producteur" class="text-muted"> ({{ scope.row.code_producteur }})</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('recolte.colCollectes')" prop="nombre_de_collecte_producteur" width="110" align="center" />
        <el-table-column :label="$t('sacs.nombreSacs')" prop="nombre_de_sac_producteur" width="100" align="center" />
        <el-table-column :label="$t('recolte.colParcelles')" prop="nombre_de_parcelle_producteur" width="110" align="center" />
        <el-table-column :label="$t('recolte.colTonnes')" min-width="110">
          <template slot-scope="scope">{{ scope.row.volume_collecte_tonnes }}</template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import Resource from '@/api/resource';
import waves from '@/directive/waves';

const recapAdminResource = new Resource('generale-recap-collect-admin');
const campagneagricoleResource = new Resource('campagne_agricoles');
const controleurResource = new Resource('controleurs');
const scoopResource = new Resource('scoops');
const uniteProductionResource = new Resource('unite_productions');
const groupementResource = new Resource('groupements');

function extractArray(resp) {
  if (!resp) {
    return [];
  }
  if (Array.isArray(resp)) {
    return resp;
  }
  if (Array.isArray(resp.data)) {
    return resp.data;
  }
  return [];
}

export default {
  name: 'RecapCollecte',
  directives: { waves },
  data() {
    return {
      loading: false,
      recap: null,
      campagneFilter: null,
      campagnes: [],
      filters: {
        controleur_id: null,
        scoops_id: null,
        unite_production_id: null,
        groupement_id: null,
      },
      controleursOptions: [],
      scoopsOptions: [],
      upsOptions: [],
      grpsOptions: [],
      producteurSearchKeyword: '',
    };
  },
  computed: {
    repartitionProducteursFiltree() {
      if (!this.recap || !Array.isArray(this.recap.repartition_par_producteur)) {
        return [];
      }
      const raw = (this.producteurSearchKeyword || '').trim().toLowerCase();
      if (!raw) {
        return this.recap.repartition_par_producteur;
      }
      return this.recap.repartition_par_producteur.filter((row) => {
        const nom = String(row.nom_producteur || '').toLowerCase();
        const code = String(row.code_producteur || '').toLowerCase();
        const id = String(row.id_producteur ?? '');
        return nom.includes(raw) || code.includes(raw) || id.includes(raw);
      });
    },
  },
  async created() {
    await this.loadCampagnes();
    await Promise.all([
      this.loadControleurs(),
      this.loadScoops(),
      this.loadUps(),
      this.loadGrps(),
    ]);
    await this.fetchRecap();
  },
  methods: {
    parcelleSuperficie(row) {
      if (row.superficie != null && row.superficie !== '') {
        return row.superficie;
      }
      if (row.superfie != null && row.superfie !== '') {
        return row.superfie;
      }
      return '—';
    },
    parcelleEstimationRendement(row) {
      const v = row.estimation_rendement != null ? row.estimation_rendement : row.rendement_estimation;
      return v != null && v !== '' ? v : '—';
    },
    ciLabel(c) {
      const n = (c.nom || '') + ' ' + (c.prenom || '');
      return n.trim() || ('CI #' + c.id);
    },
    tagType(label) {
      const m = {
        rouge: 'danger',
        orange: 'warning',
        vert: 'success',
        noir: 'info',
      };
      return m[label] || 'info';
    },
    goProducteur(id) {
      this.$router.push({ path: '/producteurs/' + id });
    },
    goParcelle(id) {
      this.$router.push({ path: '/parcelles/' + id });
    },
    getCurrentCampagneId() {
      if (this.campagneFilter != null && Array.isArray(this.campagnes) && this.campagnes[this.campagneFilter]) {
        const id = Number(this.campagnes[this.campagneFilter].id);
        return Number.isFinite(id) && id > 0 ? id : null;
      }
      return null;
    },
    buildQueryParams() {
      const params = {};
      const campId = this.getCurrentCampagneId();
      if (campId) {
        params.campagne_id = campId;
      }
      if (this.filters.controleur_id) {
        params.controleur_id = this.filters.controleur_id;
      }
      if (this.filters.scoops_id) {
        params.scoops_id = this.filters.scoops_id;
      }
      if (this.filters.unite_production_id) {
        params.unite_production_id = this.filters.unite_production_id;
      }
      if (this.filters.groupement_id) {
        params.groupement_id = this.filters.groupement_id;
      }
      return params;
    },
    async fetchRecap() {
      this.loading = true;
      try {
        const res = await recapAdminResource.list(this.buildQueryParams());
        if (res && res.success && res.data) {
          this.recap = res.data;
          this.producteurSearchKeyword = '';
        } else {
          this.recap = null;
          this.producteurSearchKeyword = '';
          this.$message.warning(this.$t('recolte.recapEmpty'));
        }
      } catch (e) {
        this.recap = null;
        this.producteurSearchKeyword = '';
        const msg = (e.response && e.response.data && e.response.data.message) || e.message || 'Erreur';
        this.$message.error(msg);
      } finally {
        this.loading = false;
      }
    },
    onCampagneChange() {
      this.fetchRecap();
    },
    resetFilters() {
      this.producteurSearchKeyword = '';
      this.filters = {
        controleur_id: null,
        scoops_id: null,
        unite_production_id: null,
        groupement_id: null,
      };
      this.setDefaultCampagne();
      this.fetchRecap();
    },
    setDefaultCampagne() {
      if (!Array.isArray(this.campagnes) || this.campagnes.length === 0) {
        return;
      }
      const indexed = this.campagnes.map((c, idx) => ({ c, idx }));
      const active = indexed.filter(x => Number(x.c.is_active) === 1);
      const best = (active.length > 0 ? active : indexed)
        .sort((a, b) => Number(b.c.id) - Number(a.c.id))[0];
      this.campagneFilter = best.idx;
    },
    async loadCampagnes() {
      const res = await campagneagricoleResource.list({ page: 1, limit: 200 });
      this.campagnes = extractArray(res);
      if (this.campagneFilter == null) {
        this.setDefaultCampagne();
      }
    },
    async loadControleurs() {
      const resp = await controleurResource.sublist('/indexactive', { limit: 500 });
      this.controleursOptions = extractArray(resp);
    },
    async loadScoops() {
      const resp = await scoopResource.sublist('/indexactive', { limit: 500 });
      this.scoopsOptions = extractArray(resp);
    },
    async loadUps() {
      const resp = await uniteProductionResource.sublist('/indexactive', { limit: 1000 });
      this.upsOptions = extractArray(resp);
    },
    async loadGrps() {
      const resp = await groupementResource.sublist('/indexactive', { limit: 1000 });
      this.grpsOptions = extractArray(resp);
    },
  },
};
</script>

<style scoped>
.kpi-row {
  margin-bottom: 8px;
}
.kpi {
  padding: 8px 0;
}
.kpi-label {
  display: block;
  color: #909399;
  font-size: 13px;
}
.kpi-val {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}
.link-like {
  color: #409eff;
  cursor: pointer;
}
.text-muted {
  color: #909399;
  font-size: 12px;
}
</style>
